import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Form, Button, Alert } from 'react-bootstrap';
import { analyticsApi } from '../services/api';
import { FiTrendingUp, FiUsers, FiDollarSign, FiCalendar, FiCheckCircle, FiXCircle, FiClock } from 'react-icons/fi';

const AnalyticsPage = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    // Set default date range (last 30 days)
    const end = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 1);
    
    setEndDate(end.toISOString().split('T')[0]);
    setStartDate(start.toISOString().split('T')[0]);
    
    fetchAnalytics(start.toISOString().split('T')[0], end.toISOString().split('T')[0]);
  }, []);

  const fetchAnalytics = async (start, end) => {
    try {
      setLoading(true);
      const response = await analyticsApi.getAnalytics({
        startDate: start,
        endDate: end
      });
      setAnalytics(response.data.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = () => {
    if (startDate && endDate) {
      fetchAnalytics(startDate, endDate);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="loading-spinner" style={{ margin: '0 auto', width: '40px', height: '40px' }}></div>
        <p className="mt-3">Loading analytics...</p>
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  if (!analytics) {
    return <Alert variant="info">No analytics data available</Alert>;
  }

  const { overview, charts } = analytics;

  return (
    <div className="analytics-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Analytics & Reports</h2>
        <div className="d-flex gap-2">
          <Form.Control
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            style={{ width: 'auto' }}
          />
          <Form.Control
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            style={{ width: 'auto' }}
          />
          <Button variant="primary" onClick={handleDateChange}>
            Update
          </Button>
        </div>
      </div>

      {/* Overview Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <FiCalendar size={32} className="text-primary mb-2" />
              <h3>{overview.totalAppointments}</h3>
              <p className="text-muted mb-0">Total Appointments</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <FiCheckCircle size={32} className="text-success mb-2" />
              <h3>{overview.completedAppointments}</h3>
              <p className="text-muted mb-0">Completed</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <FiDollarSign size={32} className="text-success mb-2" />
              <h3>${overview.totalRevenue}</h3>
              <p className="text-muted mb-0">Total Revenue</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <FiUsers size={32} className="text-primary mb-2" />
              <h3>{overview.uniqueCustomers}</h3>
              <p className="text-muted mb-0">Unique Customers</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Secondary Metrics */}
      <Row className="mb-4">
        <Col md={4}>
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1">Conversion Rate</p>
                  <h4>{overview.conversionRate}%</h4>
                </div>
                <FiTrendingUp size={24} className="text-success" />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1">Avg. Appointments/Day</p>
                  <h4>{overview.avgAppointmentsPerDay}</h4>
                </div>
                <FiCalendar size={24} className="text-primary" />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1">Repeat Customers</p>
                  <h4>{overview.repeatCustomers}</h4>
                </div>
                <FiUsers size={24} className="text-primary" />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Status Breakdown */}
      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Appointments by Status</h5>
            </Card.Header>
            <Card.Body>
              <div className="d-flex justify-content-between mb-2">
                <span>Completed</span>
                <strong>{overview.completedAppointments}</strong>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>Cancelled</span>
                <strong>{overview.cancelledAppointments}</strong>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>No-Show</span>
                <strong>{overview.noShowAppointments}</strong>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Top Services</h5>
            </Card.Header>
            <Card.Body>
              {charts.appointmentsByService.slice(0, 5).map((item, index) => (
                <div key={item.serviceId} className="d-flex justify-content-between mb-2">
                  <span>{index + 1}. {item.serviceName}</span>
                  <strong>{item.count}</strong>
                </div>
              ))}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Peak Hours */}
      {charts.peakHours && charts.peakHours.length > 0 && (
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">Peak Booking Hours</h5>
          </Card.Header>
          <Card.Body>
            <Row>
              {charts.peakHours.map((item) => (
                <Col md={2} key={item.hour} className="text-center mb-3">
                  <div className="p-3 bg-light rounded">
                    <h5>{item.hour}:00</h5>
                    <p className="mb-0">{item.count} appointments</p>
                  </div>
                </Col>
              ))}
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Appointments by Day Chart (Simple Table) */}
      {charts.appointmentsByDay && charts.appointmentsByDay.length > 0 && (
        <Card>
          <Card.Header>
            <h5 className="mb-0">Appointments by Day</h5>
          </Card.Header>
          <Card.Body>
            <div className="table-responsive">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Appointments</th>
                  </tr>
                </thead>
                <tbody>
                  {charts.appointmentsByDay.map((item) => (
                    <tr key={item.date}>
                      <td>{new Date(item.date).toLocaleDateString()}</td>
                      <td>{item.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default AnalyticsPage;
